import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as ReportIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchMyVisitReports } from '../../store/slices/visitReportSlice';

interface VisitReport {
  id: number;
  reportNumber: string;
  doctorName: string;
  doctorSpecialization: string;
  patientName: string;
  visitDate: string;
  diagnosis: string;
  status: string;
  statusDisplayName: string;
  hasPrescription: boolean;
  createdAt: string;
}

const VisitReportListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { reports, loading, error } = useSelector((state: RootState) => state.visitReport);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(fetchMyVisitReports() as any);
  }, [dispatch]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return { bg: '#d1fae5', color: '#065f46' };
      case 'PENDING': return { bg: '#fef3c7', color: '#92400e' };
      case 'CANCELLED': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const filteredReports = (reports || []).filter((r: VisitReport) => {
    const matchesSearch = !searchQuery ||
      r.reportNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.doctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.diagnosis?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Medical Records</Typography>
          <Typography variant="body2" color="#6b7280">View your visit reports and medical history</Typography>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by report number, doctor, or diagnosis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            >
              <option value="">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="body2" color="#6b7280">{filteredReports.length} records</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress />}

      {/* Reports Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Report #</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Visit Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Diagnosis</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Prescription</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                    <ReportIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                    <Typography color="#6b7280">No medical records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report: VisitReport) => (
                  <TableRow key={report.id} hover sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'transparent' , color: '#92400e' }}>
                          <ReportIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                            {report.reportNumber}
                          </Typography>
                          <Typography variant="caption" color="#6b7280">{report.doctorSpecialization}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{report.doctorName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">{report.visitDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#374151">{report.diagnosis || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      {report.hasPrescription ? (
                        <Chip label="Yes" size="small" color="success" sx={{ fontWeight: 500 }} />
                      ) : (
                        <Chip label="No" size="small" sx={{ bgcolor: 'transparent' , color: '#6b7280', fontWeight: 500 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.statusDisplayName || report.status}
                        size="small"
                        sx={{ bgcolor: getStatusColor(report.status).bg, color: getStatusColor(report.status).color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => navigate(`/visit-reports/${report.id}`)} sx={{ color: '#2563eb' }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#059669' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default VisitReportListPage;